import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { fetchMyPermissions } from "../../store/slices/permissionsSlice";
import { formatModuleDisplayName } from "../../utils/moduleDisplayName";
import { formatPermissionDisplay } from "../../utils/permissionDisplayName";
import {
  usePermissionMatrix,
  useBulkUpdatePermissions,
  useAllRolesWithStats,
  useAllModules,
  useExportPermissions,
  useImportPermissions,
  useSeedDefaultPermissions,
  useCreateRole,
} from "../../hooks/queries/useRBAC";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  Divider,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Save,
  Refresh,
  Download,
  Upload,
  Add,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
  UnfoldLess,
} from "@mui/icons-material";
import Toast from "../../components/common/Toast";
import rbacService from "../../services/rbacService";

// Small memoized cell to avoid re-rendering entire table when one checkbox changes
const PermissionCell = React.memo(
  ({ checked, onToggle, title, disabled, color }) => {
    return (
      <Tooltip title={title}>
        <Checkbox
          checked={checked}
          onChange={onToggle}
          disabled={disabled}
          size="small"
          sx={{
            p: 0,
            color: color,
            "&.Mui-checked": {
              color: color,
            },
            "&.MuiCheckbox-indeterminate": {
              color: color,
            },
          }}
        />
      </Tooltip>
    );
  },
  (prev, next) => prev.checked === next.checked && prev.disabled === next.disabled && prev.color === next.color && prev.title === next.title
);

const RBAC_MATRIX_COLLAPSED_KEY = "erp-rbac-matrix-collapsed-v1";

/** Matrix column key — role slug alone collides when global + institute roles share a name (e.g. two "student"). */
const roleColKey = (role) => String(role?.id ?? role?.roleId ?? role?.role ?? "");

function loadCollapsedModuleSet() {
  try {
    const raw = localStorage.getItem(RBAC_MATRIX_COLLAPSED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.map(String));
  } catch (_) {
    /* ignore */
  }
  return null;
}

function saveCollapsedModuleSet(set) {
  try {
    localStorage.setItem(RBAC_MATRIX_COLLAPSED_KEY, JSON.stringify([...set]));
  } catch (_) {
    /* ignore */
  }
}

const RolesPermissions = () => {
  const { user } = useSelector((s) => s.auth || {});
  const rawRole = user?.role?.slug || user?.role || "";
  const normalizedRole = String(rawRole || "").toLowerCase().trim();
  const isSuperAdminUser = normalizedRole === "superadmin" || normalizedRole === "super admin";
  const [isSaving, setIsSaving] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [permissionIndicators, setPermissionIndicators] = useState({});
  const [initialPermissions, setInitialPermissions] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [syncingAllInstitute, setSyncingAllInstitute] = useState(false);
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [createRoleForm, setCreateRoleForm] = useState({
    name: "",
    slug: "",
    description: "",
    hierarchyLevel: 0,
    permissionIds: [],
  });
  const [collapsedModules, setCollapsedModules] = useState(() => loadCollapsedModuleSet() || new Set());

  // API hooks
  const { data: permissionMatrix, isLoading: matrixLoading, error: matrixError, refetch: refetchPermissionMatrix } = usePermissionMatrix();
  const { data: rolesData, isLoading: rolesLoading, error: rolesError, refetch: refetchRoles } = useAllRolesWithStats();
  const { data: modulesData, isLoading: modulesLoading, error: modulesError } = useAllModules();

  const bulkUpdateMutation = useBulkUpdatePermissions();
  const exportQuery = useExportPermissions();
  const importMutation = useImportPermissions();
  const seedMutation = useSeedDefaultPermissions();
  const createRoleMutation = useCreateRole();

  const dispatch = useDispatch();

  useEffect(() => {
    if (permissionMatrix?.data?.modules) {
      setCollapsedModules((prev) => {
        const stored = loadCollapsedModuleSet();
        if (stored) return stored;
        if (prev && prev.size > 0) return prev;
        return new Set(permissionMatrix.data.modules.map((m) => String(m.module)));
      });
      const newPermissions = {};
      const newIndicators = {};
      permissionMatrix.data.modules.forEach(module => {
        module.permissions.forEach(perm => {
          newPermissions[perm.slug] = {};
          newIndicators[perm.slug] = {};
          perm.roles.forEach((r) => {
            const colKey = String(r.roleId ?? r.role);
            newPermissions[perm.slug][colKey] = r.hasPermission === true;
            newIndicators[perm.slug][colKey] = {
              overridden: r.overridden === true,
              source: r.source || null,
              inherited: r.inherited === true,
            };
          });
        });
      });
      setPermissions(newPermissions);
      setPermissionIndicators(newIndicators);
      setInitialPermissions(JSON.stringify(newPermissions));
    }
  }, [permissionMatrix]);

  const toggleModuleCollapsed = useCallback((moduleKey) => {
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      const key = String(moduleKey);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveCollapsedModuleSet(next);
      return next;
    });
  }, []);

  const expandAllModules = useCallback(() => {
    setCollapsedModules(() => {
      const next = new Set();
      saveCollapsedModuleSet(next);
      return next;
    });
  }, []);

  const collapseAllModules = useCallback(() => {
    const keys = (permissionMatrix?.data?.modules || []).map((m) => String(m.module));
    setCollapsedModules(() => {
      const next = new Set(keys);
      saveCollapsedModuleSet(next);
      return next;
    });
  }, [permissionMatrix?.data?.modules]);

  const isDirty = useMemo(() => {
    if (!initialPermissions) return false;
    try {
      return JSON.stringify(permissions) !== initialPermissions;
    } catch (e) {
      return false;
    }
  }, [permissions, initialPermissions]);

  const roles = useMemo(() => {
    const fromMatrix = permissionMatrix?.data?.roles;
    if (Array.isArray(fromMatrix) && fromMatrix.length > 0) return fromMatrix;
    const fallback = rolesData?.data?.roles || rolesData?.data?.data?.roles || rolesData?.roles || [];
    if (Array.isArray(fallback) && fallback.length > 0) return fallback;
    return [
      { role: "student", displayName: "Student" },
      { role: "teacher", displayName: "Teacher" },
      { role: "hod", displayName: "HOD" },
      { role: "admin", displayName: "Admin" },
      { role: "superAdmin", displayName: "Super Admin" },
    ];
  }, [permissionMatrix, rolesData]);

  const roleMetaBySlug = useMemo(() => {
    const list = rolesData?.data?.roles || rolesData?.data?.data?.roles || rolesData?.roles || [];
    const out = new Map();
    if (Array.isArray(list)) {
      for (const r of list) {
        const slug = r.slug || r.role;
        if (!slug) continue;
        out.set(String(slug), {
          instituteId: r.instituteId ?? null,
          isSystem: r.isSystem ?? false,
        });
      }
    }
    return out;
  }, [rolesData]);



  const handlePermissionChange = useCallback((permissionKey, role) => {
    if (bulkUpdateMutation.isLoading) return;
    if (!permissionKey || !role) {
      console.warn('Invalid permission or role:', { permissionKey, role });
      return;
    }
    setPermissions((prev) => {
      const newPermissions = { ...prev };
      if (!newPermissions[permissionKey]) {
        newPermissions[permissionKey] = {};
      }
      newPermissions[permissionKey] = {
        ...newPermissions[permissionKey],
        [role]: !newPermissions[permissionKey][role],
      };
      return newPermissions;
    });
  }, [bulkUpdateMutation.isLoading]);

  useEffect(() => {
    setIsSaving(!!bulkUpdateMutation.isLoading);
  }, [bulkUpdateMutation.isLoading]);

  const permKeys = useMemo(() => Object.keys(permissions || {}), [permissions]);

  const roleSelectionMap = useMemo(() => {
    const map = {};
    permKeys.forEach(() => {});
    (roles || []).forEach((role) => {
      const colKey = roleColKey(role);
      const all = permKeys.length > 0 && permKeys.every((pk) => !!permissions[pk]?.[colKey]);
      const some = permKeys.length > 0 && permKeys.some((pk) => !!permissions[pk]?.[colKey]);
      map[colKey] = { all, some: some && !all };
    });
    return map;
  }, [permissions, roles, permKeys]);

  const isRoleAllSelected = (colKey) => !!roleSelectionMap[colKey]?.all;
  const isRoleSomeSelected = (colKey) => !!roleSelectionMap[colKey]?.some;

  const handleRoleSelectAll = useCallback((colKey) => {
    if (bulkUpdateMutation.isLoading) return;
    const selectAll = !isRoleAllSelected(colKey);
    setPermissions(prev => {
      const next = { ...prev };
      permKeys.forEach(pk => {
        next[pk] = { ...(next[pk] || {}) };
        next[pk][colKey] = selectAll;
      });
      return next;
    });
  }, [bulkUpdateMutation.isLoading, permKeys, isRoleAllSelected]);

  const handleSave = () => {
    const initial = initialPermissions ? JSON.parse(initialPermissions) : {};
    const rolePermissions = [];

    (roles || []).forEach((r) => {
      const colKey = roleColKey(r);
      const currentAssigned = permKeys.filter((pk) => !!(permissions[pk] && permissions[pk][colKey]));
      const initialAssigned = Object.keys(initial || {}).filter((pk) => !!(initial[pk] && initial[pk][colKey]));

      const added = currentAssigned.filter((s) => !initialAssigned.includes(s));
      const removed = initialAssigned.filter((s) => !currentAssigned.includes(s));

      if (added.length > 0 || removed.length > 0) {
        rolePermissions.push({
          roleId: r.id ?? r.roleId,
          role: r.role,
          permissionSlugs: currentAssigned,
        });
      }
    });

    // If nothing changed, don't show loader or call API
    if (rolePermissions.length === 0) {
      setToastMessage("No changes to save");
      setToastSeverity("info");
      return;
    }

    // Show loader immediately until API responds
    setIsSaving(true);

    bulkUpdateMutation.mutate({ rolePermissions, cascadeToInheritors: true, replaceExisting: isSuperAdminUser }, {
      onSuccess: (response) => {
        // stop loader
        setIsSaving(false);

        const msg =
          response?.data?.message ||
          response?.message ||
          "Permissions saved successfully!";
        setToastMessage(msg);
        setToastSeverity("success");
        refetchPermissionMatrix();
        // Refresh current user's permissions so UI updates immediately
        try { dispatch(fetchMyPermissions()); } catch (e) {}
      },
      onError: (error) => {
        // stop loader
        setIsSaving(false);

        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save permissions";
        setToastMessage(msg);
        setToastSeverity("error");
        console.error("Failed to save permissions:", error);
      }
    });
  };

  const handleSyncWithGlobal = useCallback(async (roleObj) => {
    try {
      if (!roleObj?.id) return;
      await rbacService.syncGlobalRolePermissions(roleObj.id);
      setToastSeverity("success");
      setToastMessage("Synced role permissions with global template.");
      try { await refetchPermissionMatrix(); } catch (e) {}
      try { await refetchRoles(); } catch (e) {}
      try { dispatch(fetchMyPermissions()); } catch (e) {}
    } catch (e) {
      setToastSeverity("error");
      setToastMessage(e?.response?.data?.message || e?.message || "Failed to sync role with global template.");
    }
  }, [refetchPermissionMatrix, refetchRoles, dispatch]);

  const handleSyncAllInstituteRoles = useCallback(async () => {
    if (isSuperAdminUser || !roles?.length) return;
    const seen = new Set();
    const ids = [];
    for (const r of roles) {
      const slug = String(r.role || "").toLowerCase().replace(/[\s_]+/g, "");
      if (slug === "superadmin" || !r.id) continue;
      const id = Number(r.id);
      if (!Number.isFinite(id) || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    if (ids.length === 0) return;
    setSyncingAllInstitute(true);
    try {
      for (const id of ids) {
        await rbacService.syncGlobalRolePermissions(id);
      }
      setToastSeverity("success");
      setToastMessage("All roles restored to global defaults.");
      await refetchPermissionMatrix();
      await refetchRoles();
      try { dispatch(fetchMyPermissions()); } catch (e) {}
    } catch (e) {
      setToastSeverity("error");
      setToastMessage(e?.response?.data?.message || e?.message || "Failed to restore defaults.");
    } finally {
      setSyncingAllInstitute(false);
    }
  }, [isSuperAdminUser, roles, refetchPermissionMatrix, refetchRoles, dispatch]);

  const handleExport = () => {
    setToastMessage('Exporting permissions...');
    setToastSeverity('info');
    exportQuery.refetch().then((result) => {
      if (result.data) {
        const dataStr = JSON.stringify(result.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const exportFileDefaultName = `permissions_export_${new Date().toISOString().split('T')[0]}.json`;
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
        setToastMessage('Permissions exported');
        setToastSeverity('success');
      }
    }).catch((error) => {
      console.error("Export failed:", error);
      setToastMessage('Export failed');
      setToastSeverity('error');
    });
  };

  const handleImport = () => {
    setToastMessage('Select a JSON file to import');
    setToastSeverity('info');
    document.getElementById('permission-import-input').click();
  };

  const handleFileImport = (event) => {
    if (importMutation.isPending) {
      event.target.value = "";
      return;
    }
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const permissionsData = JSON.parse(e.target.result);
          setToastMessage('Import started');
          setToastSeverity('info');
          importMutation.mutate(permissionsData, {
            onSuccess: () => {
              setToastMessage('Import successful');
              setToastSeverity('success');
              refetchPermissionMatrix();
              refetchRoles();
              try { dispatch(fetchMyPermissions()); } catch (e) {}
            },
            onError: (error) => {
              console.error("Import failed:", error);
              setToastMessage('Import failed');
              setToastSeverity('error');
            }
          });
        } catch (error) {
          console.error("Invalid JSON file:", error);
          setToastMessage('Invalid JSON file');
          setToastSeverity('error');
        }
      };
      reader.readAsText(file);
    }
    event.target.value = "";
  };

  const handleReset = () => {
    setToastMessage('Resetting to default...');
    setToastSeverity('info');
    seedMutation.mutate({ replace: true }, {
      onSuccess: () => {
        setToastMessage('Reset successful');
        setToastSeverity('success');
        refetchPermissionMatrix();
        refetchRoles();
        try { dispatch(fetchMyPermissions()); } catch (e) {}
      },
      onError: (err) => {
        console.error('Reset failed', err);
        setToastMessage('Reset failed');
        setToastSeverity('error');
      }
    });
  };

  const getRoleColumnColor = (role) => {
    const slug = String(role?.role || "").toLowerCase().replace(/[\s_]+/g, "");
    if (slug === "superadmin") return "#1a237e";
    if (slug === "admin" || slug === "institute") return "#00695c";
    if (slug === "hod") return "#e65100";
    if (slug === "teacher" || slug === "lecturer" || slug === "professor") return "#2e7d32";
    if (slug === "student") return "#1565c0";
    if (slug === "worker") return "#6a1b9a";
    if (slug === "test") return "#546e7a";
    switch (role?.displayName) {
      case "Super Admin":
        return "#1a237e";
      case "Admin":
        return "#00695c";
      case "HOD":
        return "#e65100";
      case "Teacher":
        return "#2e7d32";
      case "Student":
        return "#1565c0";
      case "Exam Controller":
        return "#7b1fa2";
      case "Librarian":
        return "#c62828";
      default:
        return "#757575";
    }
  };

  const featureColWidth = 280;
  const roleColWidth = 120;
  const tableMinWidth = featureColWidth + (roles?.length || 0) * roleColWidth;

  const headerCellSx = {
    fontWeight: "bold",
    bgcolor: "#263238",
    color: "white",
    verticalAlign: "middle",
    py: 1,
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  };

  const roleHeaderCellSx = {
    ...headerCellSx,
    width: roleColWidth,
    minWidth: roleColWidth,
    maxWidth: roleColWidth,
    textAlign: "center",
    px: 0.5,
  };

  const roleBodyCellSx = {
    width: roleColWidth,
    minWidth: roleColWidth,
    maxWidth: roleColWidth,
    textAlign: "center",
    verticalAlign: "middle",
    px: 0.5,
    py: 0.5,
  };

  return (
    <Box>
      {/* Info Alert - Explaining this is default template */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Default Roles & Permissions Template</strong>
          <br />
          These settings define the default roles and permissions that will be
          automatically applied when a new institute is created. Each institute
          can later customize their own roles via their admin panel.
        </Typography>
      </Alert>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {isSuperAdminUser ? "Default Roles & Permissions" : "Institute Roles & Permissions"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSuperAdminUser
              ? "Configure default access permissions that will be applied to all new institutes"
              : "Configure institute-level permission overrides for roles"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isSuperAdminUser ? (
            <>
              <Chip
                label="SUPER ADMIN"
                sx={{ bgcolor: "#1a237e", color: "white", fontWeight: "bold" }}
              />
              <Chip label="Platform Defaults" variant="outlined" color="primary" />
            </>
          ) : (
            <>
              <Chip label="INSTITUTE" sx={{ bgcolor: "#004d40", color: "white", fontWeight: "bold" }} />
              <Chip label="Overrides" variant="outlined" color="primary" />
            </>
          )}
        </Box>
      </Box>

      {(matrixLoading || rolesLoading || modulesLoading) && (
        <Typography>Loading permissions...</Typography>
      )}
      {(matrixError || rolesError || modulesError) && (
        <Typography color="error">Error loading permissions data</Typography>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || syncingAllInstitute || !isDirty}
        >
          {isSaving && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
        {!isSuperAdminUser && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Refresh />}
            onClick={handleSyncAllInstituteRoles}
            disabled={isSaving || syncingAllInstitute}
          >
            {syncingAllInstitute ? "Restoring..." : "Restore all to global defaults"}
          </Button>
        )}
        {isSuperAdminUser && (
          <Button variant="outlined" startIcon={<Refresh />} onClick={handleReset} disabled={isSaving || syncingAllInstitute}>
            Reset to Default
          </Button>
        )}
        {isSuperAdminUser && (
          <Button variant="outlined" startIcon={<Download />} onClick={handleExport} disabled={isSaving || syncingAllInstitute}>
            Export Permissions
          </Button>
        )}
        {isSuperAdminUser && (
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={handleImport}
            disabled={isSaving || syncingAllInstitute || importMutation.isPending}
          >
            Import Permissions
          </Button>
        )}
        {/* <Button
          variant="contained"
          color="secondary"
          startIcon={<Add />}
          onClick={() => setCreateRoleDialogOpen(true)}
          disabled={isSaving}
        >
          Create Role
        </Button> */}
        {isDirty && (
          <Chip label="Unsaved changes" color="warning" variant="outlined" sx={{ ml: 1 }} />
        )}
      </Box>

      {/* Hidden file input for import */}
      <input
        type="file"
        id="permission-import-input"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileImport}
      />

      {/* Permission Matrix */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, bgcolor: "grey.100", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {isSuperAdminUser ? "Default Permission Matrix" : "Institute Permission Matrix"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isSuperAdminUser
                  ? "These permissions will be copied to each new institute as their starting configuration"
                  : "These permissions override global defaults for your institute only"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<UnfoldMore />} onClick={expandAllModules}>
                Expand all
              </Button>
              <Button size="small" variant="outlined" startIcon={<UnfoldLess />} onClick={collapseAllModules}>
                Collapse all
              </Button>
            </Box>
          </Box>
          {/* Single scroll area: sticky header + body share column widths (no duplicate x scrollbar) */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              maxHeight: "70vh",
              overflow: "auto",
              maxWidth: "100%",
            }}
          >
            <Table sx={{ minWidth: tableMinWidth, tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      ...headerCellSx,
                      width: featureColWidth,
                      minWidth: featureColWidth,
                      maxWidth: featureColWidth,
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 6,
                      textAlign: "left",
                    }}
                  >
                    Feature / Permission
                  </TableCell>
                  {roles.map((role) => (
                    <TableCell
                      key={roleColKey(role)}
                      sx={{
                        ...roleHeaderCellSx,
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isRoleAllSelected(roleColKey(role))}
                          indeterminate={isRoleSomeSelected(roleColKey(role)) && !isRoleAllSelected(roleColKey(role))}
                          onChange={() => handleRoleSelectAll(roleColKey(role))}
                          disabled={bulkUpdateMutation.isLoading || (!isSuperAdminUser && String(role.role || "").toLowerCase().replace(/\s/g, "") === "superadmin")}
                          sx={{
                            p: 0,
                            color: getRoleColumnColor(role),
                            "&.Mui-checked": { color: getRoleColumnColor(role) },
                          }}
                        />
                        <Tooltip title={role.displayName || ""} arrow>
                          <Chip
                            title={role.displayName || ""}
                            label={role.displayName}
                            size="small"
                            sx={{
                              bgcolor: getRoleColumnColor(role),
                              color: "white",
                              fontWeight: "bold",
                              maxWidth: roleColWidth - 8,
                              justifyContent: "center",
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                px: 0.5,
                                textAlign: "center",
                              },
                            }}
                          />
                        </Tooltip>
                        {!isSuperAdminUser && (
                          <Chip
                            label={role.scope === "INSTITUTE" ? (role.isOverridden ? "Override" : "Institute") : "Global"}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: 10,
                              height: 18,
                              color: "white",
                              borderColor: "rgba(255,255,255,0.6)",
                            }}
                          />
                        )}
                        {!isSuperAdminUser &&
                          String(role.role || "").toLowerCase().replace(/[\s_]+/g, "") !== "superadmin" && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleSyncWithGlobal(role)}
                            disabled={isSaving || syncingAllInstitute}
                            sx={{
                              minWidth: 0,
                              px: 0.5,
                              py: 0,
                              fontSize: 10,
                              lineHeight: 1.2,
                              color: "rgba(255,255,255,0.9)",
                              textTransform: "none",
                            }}
                          >
                            Sync
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixLoading ? (
                  <TableRow>
                    <TableCell colSpan={roles.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography>Loading permissions...</Typography>
                    </TableCell>
                  </TableRow>
                ) : matrixError ? (
                  <TableRow>
                    <TableCell colSpan={roles.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography color="error">Error loading permissions</Typography>
                    </TableCell>
                  </TableRow>
                ) : !permissionMatrix?.data?.modules || permissionMatrix.data.modules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={roles.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography>No permissions found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  permissionMatrix.data.modules.map((module, catIndex) => {
                    const moduleKey = String(module.module);
                    const isCollapsed = collapsedModules.has(moduleKey);
                    return (
                    <React.Fragment key={module.module}>
                    {/* Category Header Row */}
                    <TableRow
                      hover
                      onClick={() => toggleModuleCollapsed(moduleKey)}
                      sx={{ cursor: "pointer", userSelect: "none" }}
                    >
                      <TableCell
                        colSpan={roles.length + 1}
                        sx={{
                          bgcolor: "#e0f2f1",
                          fontWeight: "bold",
                          fontSize: "0.95rem",
                          py: 1.5,
                          position: "sticky",
                          left: 0,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                          <span>{module.displayName || formatModuleDisplayName(module.module)}</span>
                          <Chip
                            size="small"
                            label={`${(module.permissions || []).length} permissions`}
                            sx={{ ml: 1, height: 22, fontSize: "0.7rem" }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                    {/* Permission Rows */}
                    {(module.permissions || []).map((permission, permIndex) => (
                      <TableRow
                        key={permission.slug || permission.key}
                        sx={{
                          display: isCollapsed ? "none" : "table-row",
                          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                          bgcolor: permIndex % 2 === 0 ? "white" : "grey.50",
                        }}
                      >
                        <TableCell
                          sx={{
                            pl: 4,
                            width: featureColWidth,
                            minWidth: featureColWidth,
                            maxWidth: featureColWidth,
                            position: "sticky",
                            left: 0,
                            bgcolor: permIndex % 2 === 0 ? "white" : "grey.50",
                            zIndex: 2,
                            verticalAlign: "middle",
                          }}
                        >
                          {formatPermissionDisplay(permission)}
                        </TableCell>
                        {roles.map((role) => {
                          const permissionSlug = permission.slug || permission.key;
                          const colKey = roleColKey(role);
                          const currentValue = !!permissions[permissionSlug]?.[colKey];
                          const indicator = permissionIndicators?.[permissionSlug]?.[colKey] || {};
                          const roleSlugLc = String(role.role || "").toLowerCase().replace(/\s/g, "");
                          const superAdminCol = roleSlugLc === "superadmin";
                          return (
                            <TableCell key={`${permissionSlug}-${colKey}`} sx={roleBodyCellSx}>
                              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                                <PermissionCell
                                  checked={currentValue}
                                  onToggle={() => handlePermissionChange(permissionSlug, colKey)}
                                  title={`${role.displayName}: ${formatPermissionDisplay(permission)}${
                                    !isSuperAdminUser && indicator.overridden ? " (customized)" : ""
                                  }${indicator.inherited ? " (also granted via a lower role in hierarchy — enable here to assign to this role)" : ""}${
                                    !isSuperAdminUser && superAdminCol ? " (not editable)" : ""
                                  }`}
                                  disabled={isSaving || (!isSuperAdminUser && superAdminCol)}
                                  color={getRoleColumnColor(role)}
                                />
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Role Legend
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {roles.map((role) => (
              <Box
                key={roleColKey(role)}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getRoleColumnColor(role),
                  }}
                />
                <Typography variant="body2">{role.displayName}</Typography>
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            {isSuperAdminUser
              ? "Note: Changes apply after saving. Super Admin has full system access; that column is not editable here."
              : "Note: Changes apply after saving. Use Restore all to global defaults or per-column Sync to clear institute overrides and match the platform template. Super Admin column is not editable."}
          </Typography>
        </CardContent>
      </Card>

      {/* Toast for save response */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />

      {/* Create Role Dialog */}
      <Dialog open={createRoleDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setCreateRoleDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Create Custom Role</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={createRoleForm.name}
              onChange={(e) => {
                const name = e.target.value;
                const slugFromName = name.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
                setCreateRoleForm((prev) => {
                  const prevSlugFromName = (prev.name || "").toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
                  const shouldUpdateSlug = !prev.slug || prev.slug === prevSlugFromName;
                  return {
                    ...prev,
                    name,
                    ...(shouldUpdateSlug ? { slug: slugFromName } : {}),
                  };
                });
              }}
              placeholder="e.g. Department Manager"
              fullWidth
            />
            <TextField
              label="Role Code"
              value={createRoleForm.slug}
              onChange={(e) => setCreateRoleForm((prev) => ({ ...prev, slug: e.target.value }))}
              placeholder="e.g. department_manager"
              fullWidth
              helperText="Unique role code; lowercase letters, numbers, and underscores"
            />
            <TextField
              label="Description"
              value={createRoleForm.description}
              onChange={(e) => setCreateRoleForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description"
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Hierarchy Level</InputLabel>
              <Select
                value={createRoleForm.hierarchyLevel}
                label="Hierarchy Level"
                onChange={(e) => setCreateRoleForm((prev) => ({ ...prev, hierarchyLevel: Number(e.target.value) }))}
              >
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n} {n === 0 ? "(lowest)" : n === 5 ? "(highest)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Permissions
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
              {(modulesData?.data || []).map((mod) => (
                <Box key={mod.code} sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ pl: 1 }}>
                    {mod.displayName}
                  </Typography>
                  {(mod.permissions || []).map((p) => (
                    <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2 }}>
                      <Checkbox
                        size="small"
                        checked={createRoleForm.permissionIds.includes(p.id)}
                        onChange={(e) => {
                          setCreateRoleForm((prev) => ({
                            ...prev,
                            permissionIds: e.target.checked
                              ? [...prev.permissionIds, p.id]
                              : prev.permissionIds.filter((id) => id !== p.id),
                          }));
                        }}
                      />
                      <Typography variant="body2">
                        {p.name || `${p.slug}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
              {(!modulesData?.data || modulesData.data.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  {modulesLoading ? "Loading permissions…" : "No permissions available. Seed defaults first."}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateRoleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!createRoleForm.name?.trim() || !createRoleForm.slug?.trim()) {
                setToastMessage("Name and role code are required");
                setToastSeverity("error");
                return;
              }
              try {
                await createRoleMutation.mutateAsync({
                  name: createRoleForm.name.trim(),
                  slug: createRoleForm.slug.trim().toLowerCase().replace(/\s+/g, "_"),
                  description: createRoleForm.description?.trim() || undefined,
                  hierarchyLevel: createRoleForm.hierarchyLevel,
                  permissionIds: createRoleForm.permissionIds,
                });
                setToastMessage("Role created successfully");
                setToastSeverity("success");
                setCreateRoleDialogOpen(false);
                setCreateRoleForm({ name: "", slug: "", description: "", hierarchyLevel: 0, permissionIds: [] });
                refetchPermissionMatrix();
                refetchRoles();
                try { dispatch(fetchMyPermissions()); } catch (e) {}
              } catch (err) {
                setToastMessage(err?.response?.data?.message || err?.message || "Failed to create role");
                setToastSeverity("error");
              }
            }}
            disabled={createRoleMutation.isLoading}
          >
            {createRoleMutation.isLoading ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPermissions;